/*
 * The Properties box: the columns on kb.Article that are not publishing gates -
 * the slug, the product and the category list - saved as one
 * optimistic-concurrency unit under the article's rowversion. And, since
 * 2026-10-26 (S46), who may read it: Visibility, Public or Private, and a
 * private article's audiences (VisibilityField) - the flag on the same row, the
 * audiences beside it, saved in the same transaction.
 *
 * THE ADDRESS IS THE PAIR (PRODUCT, SLUG). kb.Article.Slug is unique only within
 * a product, under UQ_Article_ProductId_Slug, so the product radio in this box is
 * not a tag: it is half of the URL, and moving the article to another product
 * changes the address exactly as retyping the slug does. Everything below that
 * used to be about "the slug changing" is about either half changing, and that is
 * deliberate rather than sloppy - to a link pointing here the two are the same
 * event.
 *
 * Editing the address has two consequences this box is responsible for making
 * visible:
 *
 *   Uniqueness. A slug another article already answers on UNDER THE SAME PRODUCT
 *   is caught three times over, cheapest first: against the addresses in use,
 *   fetched once the address is first edited and filtered to the chosen product,
 *   so the error appears while typing; by the server before its write, which
 *   answers 409 with the other article's name and this box puts that under the
 *   field rather than in a banner; and by UQ_Article_ProductId_Slug itself, for
 *   the race neither of the first two can see. The first two are conveniences.
 *   The third is the rule. Note what the product filter buys: without it the same
 *   slug free under the product the author just chose would be reported as taken,
 *   and the author would rename an address that was never in conflict.
 *
 *   Inbound links. Other articles may link here as [[kb:old-product/old-slug]].
 *   The server rewrites those to the new address in the same transaction as the
 *   rename and the response says how many, which is what the notice after a save
 *   reports. The one text the server deliberately does not touch is the draft open
 *   in the Text card beside this box - appending a revision to it would discard
 *   what is being typed - so that one is repaired here in the browser through
 *   `onAddressRenamed`, and the author saves it as their own revision.
 *
 *   The warning BEFORE the save counts from `incomingReferences`, which the page
 *   fetches on its own request beside the article - finding them scans every
 *   other article and must not hold the editor's open - so it can still be on
 *   its way when the author starts typing. Null means exactly that, and the
 *   warning says "still being checked" rather than the "keep working" it would
 *   say for an empty list; the notice after the save is the server's count and
 *   is exact either way.
 *
 * NOT RE-SEEDED FROM THE PROP. Saving the text bumps the article's rowversion (the
 * modified-at trigger touches the row), and an effect watching it would throw away
 * property edits somebody had typed but not yet saved. The rowversion sent on save
 * is read from the prop, not from state, so it is always the current one without
 * this form having to track it.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  AdminArticleDetail,
  ArticleLinkSlugTarget,
  ArticleReference,
  ReferenceRewriteSummary,
} from '@accotool-kb/shared';
import {
  ARTICLE_SLUG_MAX_LENGTH,
  articleHref,
  countArticlesLinkingBySlug,
  isValidSlug,
  toSlug,
} from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction } from '../../hooks/useAsync.js';
import { ErrorBanner, Field } from '../../components/ui.js';
import type { ArticleAddressInUse, MetadataBundle } from '../../api/adminApi.js';
import { ApiError } from '../../api/client.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';
import type { articlesEn } from '../../i18n/admin/articles.en.js';
import { ProductSelect } from './ProductSelect.js';
import { VisibilityField, splitStoredAudiences } from './VisibilityField.js';
import { SlugPrefix } from './SlugPrefix.js';
import { TagMultiSelect } from './TagMultiSelect.js';

export function ArticleProperties({
  article,
  metadata,
  incomingReferences,
  onUpdated,
  onAddressRenamed,
}: {
  article: AdminArticleDetail;
  metadata: MetadataBundle | null;
  /**
   * The other articles linking here, for the warning before a rename. Null
   * while the page's request for them is still out - see the header.
   */
  incomingReferences: ArticleReference[] | null;
  onUpdated: (next: AdminArticleDetail) => void;
  /**
   * Called after a save that changed the ADDRESS - the slug, the product, or
   * both - with the old and the new one, so the owner of the draft can point its
   * own [[kb:old-product/old-slug]] tokens at the new address. Returns how many
   * it rewrote, for the notice.
   */
  onAddressRenamed: (previous: ArticleLinkSlugTarget, next: ArticleLinkSlugTarget) => number;
}): JSX.Element {
  const t = useMessages();
  const shared = t.admin.articles.shared;
  const words = t.admin.articles.properties;
  const api = useAdminApi();
  const action = useAction();

  const [slug, setSlug] = useState(article.slug);
  const [productId, setProductId] = useState(article.product.productId);
  const [categoryIds, setCategoryIds] = useState(article.categories.map((tag) => tag.categoryId));
  // Who may read it (S46). Seeded once, like the fields above; a stored key the
  // server no longer configures is left out of what is sent, and named.
  const audienceOptions = metadata?.audiences ?? null;
  const storedAudiences = splitStoredAudiences(article.audiences, audienceOptions);
  const [visibility, setVisibility] = useState(article.visibility);
  const [audienceKeys, setAudienceKeys] = useState<string[] | null>(null);
  // Null until edited: until then the article's own (configured) keys, which
  // depend on the audience list - and that may arrive after the first paint.
  const chosenAudiences = audienceKeys ?? storedAudiences.keep;
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const slugEdited = slug !== article.slug;
  const productEdited = productId !== article.product.productId;
  const addressEdited = slugEdited || productEdited;

  /*
   * The product's slug, for the prefix beside the Slug box.
   *
   * The article's own product answers from the article, not from the catalogue,
   * so the address is complete on the first paint - `metadata` arrives on its own
   * request and would otherwise leave the prefix reading "/kb/.../" for an article
   * that has never been anything but assigned. Any OTHER product has to come from
   * the catalogue, because the wire shape carries one product per article and that
   * is the one it carries.
   */
  const productSlug =
    productId === article.product.productId
      ? article.product.slug
      : (metadata?.products.find((product) => product.productId === productId)?.slug ?? null);

  /*
   * The addresses in use, fetched the first time the address is edited and dropped
   * after a save - the list would by then be missing the address just taken and
   * still carrying the one just freed. A failed fetch is not worth an error: the
   * server checks again before it writes, and the field simply loses its early
   * warning.
   *
   * Fetched on a PRODUCT change too, not only on a slug edit. Moving an article to
   * another product can collide without a character of the slug changing, and that
   * collision is the one an author would least expect to be told about by a 409.
   */
  const [takenAddresses, setTakenAddresses] = useState<ArticleAddressInUse[] | null>(null);
  useEffect(() => {
    if (!addressEdited || takenAddresses !== null) return;
    let cancelled = false;
    api
      .articleSlugs()
      .then((addresses) => {
        if (!cancelled) setTakenAddresses(addresses);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, addressEdited, takenAddresses]);

  /*
   * Narrowed to the chosen product, and re-narrowed when that choice moves: a
   * slug is only taken relative to a product, so this set has to be rebuilt by
   * the same gesture that changes which product it is relative to.
   */
  const takenInProduct = useMemo(
    () =>
      takenAddresses === null
        ? null
        : new Set(
            takenAddresses
              .filter((address) => address.productId === productId)
              .map((address) => address.slug),
          ),
    [takenAddresses, productId],
  );

  const visibilityEdited =
    visibility !== article.visibility ||
    (visibility === 'private' && !sameKeys(chosenAudiences, storedAudiences.keep));

  const dirty =
    addressEdited ||
    visibilityEdited ||
    !sameSet(
      categoryIds,
      article.categories.map((tag) => tag.categoryId),
    );

  // The server's verdict on the last attempt, when that verdict was "taken". Shown
  // under the field instead of in the banner, and cleared the moment EITHER half
  // of the address moves - the slug or the product - because the verdict was about
  // the pair that was sent and not about the pair the form now holds.
  const duplicateFromServer =
    action.error instanceof ApiError && action.error.conflict === 'duplicate'
      ? action.error
      : null;

  // Three sources for one field, in the order they should win: the local rule,
  // the addresses in use under the chosen product, then what the server said.
  const slugError = !isValidSlug(slug, ARTICLE_SLUG_MAX_LENGTH)
    ? t.admin.rules.slugRequirement
    : addressEdited && takenInProduct?.has(slug)
      ? words.slugTaken
      : duplicateFromServer
        ? (duplicateFromServer.fieldError('slug') ?? duplicateFromServer.message)
        : undefined;
  const categoryError = categoryIds.length === 0 ? shared.categoryRequired : undefined;
  // The server's own words, and its verdict on a key it does not configure.
  const audienceRefused =
    action.error instanceof ApiError ? action.error.fieldError('audienceKeys') : undefined;
  const audienceError =
    visibility === 'private' && chosenAudiences.length === 0
      ? t.admin.articles.visibility.audienceRequired
      : audienceRefused;

  // The article's address as it stands, which is what a rename moves away from.
  const currentAddress: ArticleLinkSlugTarget = {
    productSlug: article.product.slug,
    slug: article.slug,
  };

  // How many other articles link here by the CURRENT address - the ones a rename
  // rewrites. A lower bound once the reference list is capped, and NULL while the
  // list has not arrived: zero and unknown are different sentences below. The
  // notice after the save is exact.
  const articlesLinkingBySlug = useMemo(
    () =>
      incomingReferences === null
        ? null
        : countArticlesLinkingBySlug(incomingReferences, currentAddress),
    [incomingReferences, currentAddress.productSlug, currentAddress.slug],
  );

  /*
   * No product error, and that is not an omission. The radio group has no gesture
   * for un-choosing, and an existing article arrives with a product because the
   * column cannot be null - so there is no state this form can reach in which the
   * field is empty. The new-article page is the one place that error is real.
   */
  const submit = async () => {
    if (slugError || categoryError || (visibility === 'private' && chosenAudiences.length === 0)) return;

    const result = await action.run(() =>
      api.updateArticle(article.articleId, {
        slug,
        productId,
        categoryIds,
        // Publishing is changed by its own panel; sending the current values keeps
        // this a pure properties update.
        publishAtUtc: article.publishAtUtc,
        isVisible: article.isVisible,
        visibility,
        audienceKeys: visibility === 'private' ? chosenAudiences : [],
        rowVersion: article.rowVersion,
      }),
    );
    if (!result) return;

    onUpdated(result.article);
    // The saved list is the article's now; editing starts from it again.
    setAudienceKeys(null);
    setTakenAddresses(null);

    // Read from the SAVED article rather than from this form's state: the product
    // slug is the catalogue's, not something typed here, and the server is the
    // party that says what was actually stored.
    const nextAddress: ArticleLinkSlugTarget = {
      productSlug: result.article.product.slug,
      slug: result.article.slug,
    };

    if (
      nextAddress.slug !== currentAddress.slug ||
      nextAddress.productSlug !== currentAddress.productSlug
    ) {
      const inDraft = onAddressRenamed(currentAddress, nextAddress);
      setSavedNotice(
        describeRename(currentAddress, nextAddress, result.referencesRenamed, inDraft, words),
      );
    }
  };

  return (
    <div className="card properties-card">
      <div className="card-header">
        <h3>{shared.properties}</h3>
      </div>

      {/* An audience refused by the server shows under its field instead (S46). */}
      {action.error && !duplicateFromServer && !audienceRefused && (
        <ErrorBanner error={action.error} onDismiss={action.clearError} />
      )}

      {savedNotice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{savedNotice}</div>
          <div className="notice-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setSavedNotice(null)}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {/*
        The hint no longer spells the address out - the prefix and the box beside
        it are the address, on screen, as it is typed. What it says instead is the
        thing neither of them can show: the SCOPE of the uniqueness rule, which
        used to read "unique across the knowledge base" and has been false since
        UQ_Article_Slug became UQ_Article_ProductId_Slug.
      */}
      <Field
        label={shared.slug}
        htmlFor="slug"
        required
        error={slugError}
        hint={words.slugHint}
      >
        <SlugPrefix productSlug={productSlug}>
          <input
            id="slug"
            type="text"
            value={slug}
            maxLength={ARTICLE_SLUG_MAX_LENGTH}
            onChange={(event) => {
              setSlug(event.target.value);
              if (duplicateFromServer) action.clearError();
            }}
            onBlur={(event) => setSlug(toSlug(event.target.value, ARTICLE_SLUG_MAX_LENGTH))}
          />
        </SlugPrefix>
      </Field>

      {/*
        Shown for a product change as well as a slug edit, and worded for both at
        once. A token spells the pair, so which half moved makes no difference to
        anything this paragraph is warning about.
      */}
      {addressEdited && (
        <p className="faint" style={{ marginTop: '-0.5rem' }}>
          {words.addressChanges}{' '}
          {articlesLinkingBySlug === null
            ? words.linksBeingChecked
            : articlesLinkingBySlug > 0
              ? words.linksWillBeRewritten(articlesLinkingBySlug)
              : richText(words.tokensKeepWorking, { token: <code>[[kb:…]]</code> })}{' '}
          {richText(words.handWrittenLinks, {
            address: <code>{articleHref(currentAddress.productSlug, currentAddress.slug)}</code>,
          })}
        </p>
      )}

      <ProductSelect
        options={(metadata?.products ?? []).map((product) => ({
          id: product.productId,
          name: product.name,
          isActive: product.isActive,
        }))}
        selected={productId}
        onChange={(next) => {
          setProductId(next);
          // The server's "taken" verdict was about the PAIR that was sent, so
          // moving the product clears it exactly as editing the slug does.
          // Without this the form can sit unsaveable on an address that is
          // free: the message stays, slugError keeps returning it, and Save
          // stays disabled - while the remedy the message implies, choosing a
          // different product, is the very thing that failed to clear it.
          if (duplicateFromServer) action.clearError();
        }}
      />

      {/* Who may read it (S46) - before the categories, which stay last because
          they take the card's spare height. */}
      <VisibilityField
        visibility={visibility}
        audienceKeys={chosenAudiences}
        onChange={(nextVisibility, nextKeys) => {
          setVisibility(nextVisibility);
          setAudienceKeys(nextKeys);
          if (action.error instanceof ApiError && action.error.fieldError('audienceKeys')) action.clearError();
        }}
        options={audienceOptions}
        unconfigured={storedAudiences.unconfigured}
        error={audienceError}
      />

      {/*
        `grow` on this one and not on Product: this is the long list - it carries
        the former section names, since those became category tags - and it is the
        last field before the button, so the card's spare height lands where the
        scrolling actually is. Product has a handful of entries, only one of which
        can be chosen; growing it would just be a taller empty box.
      */}
      <TagMultiSelect
        grow
        label={shared.categoryTags}
        kind="category"
        error={categoryError}
        options={(metadata?.categories ?? []).map((category) => ({
          id: category.categoryId,
          name: category.name,
          isActive: category.isActive,
        }))}
        selected={categoryIds}
        onChange={setCategoryIds}
      />

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void submit()}
          disabled={
            !dirty ||
            action.running ||
            Boolean(slugError || categoryError) ||
            (visibility === 'private' && chosenAudiences.length === 0)
          }
        >
          {action.running ? shared.saving : words.save}
        </button>
        {dirty && <span className="unsaved">{shared.unsavedChanges}</span>}
      </div>
    </div>
  );
}

/**
 * The sentence after a rename. Always names the new address; then what happened
 * to the other articles, in numbers, because "links were updated" without a count
 * is indistinguishable from nothing having happened; then the draft, only when it
 * was touched, because that one still needs the author to save it.
 *
 * Both addresses come from `articleHref` rather than being spelled here, which is
 * what keeps this notice honest: it prints the URL a reader would actually visit,
 * not this file's idea of one.
 *
 * Release notes (S42, 2026-10-12): the server renames their slug-form tokens in
 * the same transaction and counts them separately (`releaseNotes`,
 * `releaseNoteTokens`, `releaseNoteRevisions`; the first three fields still count
 * other ARTICLES only). When no release note was touched the sentence is exactly
 * what it was; when one was, the links and revisions are the two kinds together
 * and the sentence names both - dropping the article clause when no article was
 * touched, so it never says "in 0 other articles". Read with `?? 0`, so a
 * summary from a server before S42 reads as "no release notes".
 *
 * Its sentences are the dictionary's (`words`, the Properties card's lines in
 * the chosen language); this decides which of them are said, and with what counts.
 */
function describeRename(
  previous: ArticleLinkSlugTarget,
  next: ArticleLinkSlugTarget,
  renamed: ReferenceRewriteSummary | null,
  inDraft: number,
  words: (typeof articlesEn)['properties'],
): string {
  const parts = [words.addressIsNow(articleHref(next.productSlug, next.slug))];

  const releaseNotes = renamed?.releaseNotes ?? 0;
  const previousHref = articleHref(previous.productSlug, previous.slug);

  if (renamed && releaseNotes > 0) {
    const links = renamed.tokens + (renamed.releaseNoteTokens ?? 0);
    const revisions = renamed.revisions + (renamed.releaseNoteRevisions ?? 0);
    parts.push(words.linksRenamed(links, renamed.articles, releaseNotes, revisions, previousHref));
  } else if (renamed && renamed.tokens > 0) {
    parts.push(words.linksRenamed(renamed.tokens, renamed.articles, 0, renamed.revisions, previousHref));
  } else {
    parts.push(words.noLinksRenamed);
  }

  if (inDraft > 0) {
    parts.push(words.draftLinksRenamed(inDraft));
  }

  return parts.join(' ');
}

function sameKeys(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const set = new Set(right);
  return left.every((value) => set.has(value));
}

function sameSet(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  const set = new Set(right);
  return left.every((value) => set.has(value));
}
